import {
  CdkVirtualForOf,
  CdkFixedSizeVirtualScroll,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';
import type { OnDestroy, ViewRef } from '@angular/core';
import { ChangeDetectorRef, Component, Input, ViewChild, output, inject } from '@angular/core';

import { Subject } from 'rxjs';

import { KeyboardEventCode } from '@core/common-legacy/keyboard';
import { TranslatePipe } from '@core/i18n-legacy';
import type { PlaceholderNavigationKey } from '@core/prosemirror-legacy';
import {
  HeadingComponent,
  DropdownItemComponent,
  DropdownHeaderComponent,
  DropdownComponent,
} from '@core/user-interface-legacy';
import type { Placeholder } from '@core/wysiwyg-legacy';

@Component({
  selector: 'rt-placeholder-dropdown-typeahead',
  templateUrl: './placeholder-dropdown-typeahead.component.html',
  styleUrls: ['./placeholder-dropdown-typeahead.component.less'],
  imports: [
    DropdownComponent,
    DropdownHeaderComponent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    DropdownItemComponent,
    HeadingComponent,
    TranslatePipe,
  ],
})
export class PlaceholderDropdownTypeaheadComponent implements OnDestroy {
  private readonly cdRef = inject(ChangeDetectorRef);

  public highlightedIndex = 0;

  public select = output<Placeholder>();
  public close = output<void>();
  @Input() public placeholders: Placeholder[];

  private ngOnDestroy$ = new Subject<void>();
  @ViewChild('scrollViewport') private cdkVirtualScrollViewport: CdkVirtualScrollViewport;

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public handleKey(key: PlaceholderNavigationKey): void {
    if (KeyboardEventCode.Enter === key) {
      const mention = this.placeholders[this.highlightedIndex];
      if (mention) {
        this.select.emit(mention);
      } else {
        this.close.emit();
      }
    } else if (KeyboardEventCode.ArrowDown === key) {
      this.highlightedIndex = (this.highlightedIndex + 1) % this.placeholders.length;
      this.cdRef.detectChanges();
    } else if (KeyboardEventCode.ArrowUp === key) {
      this.highlightedIndex =
        this.highlightedIndex === 0
          ? this.placeholders.length - 1
          : (this.highlightedIndex - 1) % this.placeholders.length;
      this.cdRef.detectChanges();
    }
    this.cdkVirtualScrollViewport.scrollToIndex(this.highlightedIndex, 'smooth');
  }

  public trackByPlaceholder(index: number, item: Placeholder) {
    return item.id;
  }

  public getContainerHeight(): string {
    const numberOfItems = this.placeholders.length;
    const itemHeight = 64;
    const visibleItems = 4.5;

    setTimeout(() => {
      this.cdkVirtualScrollViewport.checkViewportSize();
      if (!(<ViewRef>this.cdRef).destroyed) {
        this.cdRef.detectChanges();
      }
    }, 300);

    if (numberOfItems <= visibleItems) {
      return `${itemHeight * numberOfItems}px`;
    }

    return `${itemHeight * visibleItems}px`;
  }

  public onScrolledIndexChange() {
    this.cdRef.detectChanges();
  }

  public withoutBrackets(str: string): string {
    return str.slice(1, -1);
  }
}
